/**
 * Game Constants
 *
 * All game mechanics constants from the Move smart contract.
 * Used by both frontend and backend for consistent game logic.
 *
 * Source: suikamon.move constants module
 */

import { SUIKAMON_RELEASE } from './appConfig.js';

// =====================================================
// Individual Constant Definitions (Single Source of Truth)
// =====================================================

// Season and versioning
//
// ── The season's MAJOR is the release number, and is not written here ──
// It is imported from `appConfig.ts`, the one place the release number lives, so the season can
// never drift from the app version again. It did: the home page said `Beta 12.25` while this said
// `Season 6.0`, and nothing checked.
//
// ── The season's MINOR is its own, on purpose ──
// It moves when the BALANCE moves, NOT with every patch note. The stamp exists so battles can be
// grouped and measured, and release 12 shipped 99 changelog entries: a stamp per entry would have
// cut the battle record into 99 slivers of nearly no rows each, which is not a balance read.
// So `Beta 13.4` and `Beta 13.7` can both be fought under `Season 13.0` — same release, same
// rules — and the season goes to `13.1` the day the dials actually change.
//
// Bumping this opens a NEW balance version: battles fought from here carry the new stamp and are
// read apart from the ones before them in every live audit.
export const SEASON_REVISION = 6;
/** Current season identifier for NFT metadata — `Season <release>.<revision>` */
export const CURRENT_SEASON = `Season ${SUIKAMON_RELEASE}.${SEASON_REVISION}`;
/** Balance version derived from CURRENT_SEASON (e.g. "Season 3.8" → "3.8") */
export const BALANCE_VERSION = CURRENT_SEASON.replace('Season ', '');

// Randomness system constants
export const RANDOM_VALUE_MAX = 100;             // Random values 0-99 range
export const MAX_RARITY = 4;                     // Rarity levels: 0-4 (5 total)
export const PROBABILITY_DECIMAL_PLACES = 1;    // Decimal places for probability percentages
export const RARITY_NAMES = ['Common', 'Rare', 'Epic', 'Legendary', 'Unique'] as const;

// Seed character slicing positions for deterministic randomness
export const SEED_RARITY_START = 0;              // Chars [0-8] for rarity selection
export const SEED_RARITY_END = 8;
export const SEED_SPECIES_START = 8;             // Chars [8-16] for species selection
export const SEED_SPECIES_END = 16;
export const SEED_FALLBACK_START = 16;           // Chars [16-24] for fallback selection
export const SEED_FALLBACK_END = 24;
export const SEED_ULTIMATE_START = 24;           // Chars [24-32] for ultimate fallback
export const SEED_ULTIMATE_END = 32;

// Checkpoint randomness (Sui Blockchain)
export const CHECKPOINT_POLL_INTERVAL_MS = 1000; // Check every 1 second
export const CHECKPOINT_INTERVAL_SECONDS = 3;    // ~3 seconds between checkpoints on Sui
export const DEFAULT_CHECKPOINTS_AHEAD = 2;      // Wait 2 checkpoints (~6 seconds) for randomness
export const DEFAULT_MAX_WAIT_MS = 30000;        // 30 second timeout for checkpoint polling

// Level constants
export const MAX_LEVEL = 100;                     // Maximum pet level
/**
 * Free-to-play tier: guest pets are level-clamped here; XP keeps accruing silently. It is the
 * top of Book One's level band (World 1, getWorldLevelBand(0).final), so a guest's pet can grow
 * through the whole free story and nothing asks them to sign up before it ends (owner,
 * 2026-10-31). freeCampaignBoundary.test.ts holds the two together.
 */
export const FREE_TIER_MAX_LEVEL = 10;

// PVP ranking constants
export const ELO_INITIAL_RATING = 1200;          // Starting ELO for every new pet (ranked PVP)

// Egg/breeding constants
export const INITIAL_EGG_LEVEL = 1;              // Eggs start at level 1
export const INITIAL_EGG_STAGE = 0;              // Stage 0 marks breeding token/egg
export const NO_INITIAL_PAYMENT = 0;             // No initial gas/Suika for eggs
export const DEFAULT_PARENT_RARITY = 0;          // Common rarity (fallback)

// Image URLs for breeding and egg assets
export const BREEDING_TOKEN_IMAGE_URL = 'https://suikamon.com/species/egg.png';
export const EGG_IMAGE_URL = 'https://suikamon.com/species/egg.png';

// =====================================================
// Game Constants (from Move contract)
// =====================================================

export const GAME_CONSTANTS = {
  // ═══════════════════════════════════════════════════
  // STAT RANGES
  // ═══════════════════════════════════════════════════
  STAT_MIN: 0,
  STAT_MAX: 100,

  // ═══════════════════════════════════════════════════
  // FEEDING ACTIONS
  // ═══════════════════════════════════════════════════
  // Meal (balanced nutrition, restores health, no cleanliness penalty)
  FEED_MEAL_HUNGER_GAIN: 45,
  FEED_MEAL_HAPPINESS_GAIN: 7,
  FEED_MEAL_HEALTH_GAIN: 4,

  // Snack (quick happiness boost, junk food reduces health)
  FEED_SNACK_HUNGER_GAIN: 22,
  FEED_SNACK_HAPPINESS_GAIN: 15,
  FEED_SNACK_HEALTH_GAIN: 0,
  FEED_SNACK_HEALTH_LOSS: 15,         // Junk food penalty
  FEED_SNACK_CLEANLINESS_LOSS: 15,

  // ═══════════════════════════════════════════════════
  // CARE ACTIONS
  // ═══════════════════════════════════════════════════
  // Clean (full bath)
  CLEAN_CLEANLINESS_GAIN: 100,
  CLEAN_HAPPINESS_GAIN: 10,
  CLEAN_HEALTH_GAIN: 3,

  // Partial Clean (poop click - no XP, just removes one poop)
  PARTIAL_CLEAN_AMOUNT: 25,

  // Train
  ENERGY_COST_TRAIN: 25,
  TRAIN_HAPPINESS_GAIN: 10, // The web app's 🎾 Play button (was 5, doubled)
  TRAIN_HEALTH_GAIN: 10,
  DISCIPLINE_TRAIN_GAIN: 30,

  // Play (Telegram-only: simple happiness boost)
  PLAY_ENERGY_COST: 20,
  PLAY_HAPPINESS_GAIN: 20,

  // Medicine
  MEDICINE_HEALTH_GAIN: 30,
  MEDICINE_HAPPINESS_PENALTY: 10,     // Happiness cost when taking medicine

  // ═══════════════════════════════════════════════════
  // XP REWARDS
  // ═══════════════════════════════════════════════════
  XP_FEED_FULL: 30,           // Feeding when hunger < 50
  XP_FEED_REDUCED: 20,         // Feeding when hunger 50-90
  XP_CLEAN: 20,               // Cleaning
  XP_HEAL_SICKNESS: 0,        // Medicine gives no XP
  CARE_STREAK_XP_PER_DAY: 1,  // Bonus applied to eligible actions per streak day without medicine
  XP_ACTIVE_CARE_PER_TICK: 0, // DISABLED - Passive XP per tick when Happy (all stats >= 25)
  XP_SHARE_BONUS: 50,         // Bonus XP for sharing evolution on social media (once per evolution)

  // Discipline XP penalty
  DISCIPLINE_LOW_THRESHOLD: 10,        // Below this: XP penalty applies
  DISCIPLINE_LOW_XP_MULTIPLIER: 0.5,   // XP multiplier when discipline < threshold (50% XP)

  // Happiness XP bonus - DISABLED
  HAPPINESS_BONUS_THRESHOLD: 75,       // Above this: XP bonus would apply (currently disabled)
  HAPPINESS_HIGH_XP_MULTIPLIER: 1.0,  // DISABLED - No bonus (was 1.2 for +20% XP) 
  // ═══════════════════════════════════════════════════
  // CONDITION THRESHOLDS
  // ═══════════════════════════════════════════════════
  // Feeding XP thresholds
  HUNGER_XP_THRESHOLD_FULL: 50,     // Below this: full XP
  HUNGER_XP_THRESHOLD_REDUCED: 90,  // 50-90: reduced XP, above: no XP

  // Neglect thresholds (trigger health decay)
  HUNGER_NEGLECT_THRESHOLD: 25,
  CLEANLINESS_DIRTY_THRESHOLD: 25,

  // Condition thresholds (overall_condition enum values)
  // Priority: SICK (1) > HUNGRY (2) > DIRTY (3) > TIRED (4) > SAD (7) > UNRULY (6) > HAPPY (5)
  HEALTH_SICK_THRESHOLD: 25,        // Below this: sick condition (1)
  HUNGER_HUNGRY_THRESHOLD: 25,      // Below this: hungry condition (2)
  CLEANLINESS_DIRTY_THRESHOLD_CONDITION: 25,  // Below this: dirty condition (3)
  ENERGY_TIRED_THRESHOLD: 25,       // Below this: tired condition (4)
  HAPPINESS_SAD_THRESHOLD: 50,      // Below this: sad condition (7)
  STAT_HAPPY_THRESHOLD: 25,         // All stats >= this: happy condition (5) + Active Care XP

  // Rest requirements
  ENERGY_REST_REQUIREMENT: 30,      // Min hunger to start rest
  ENERGY_REST_MAX_THRESHOLD: 80,    // Max energy to start rest

  // ═══════════════════════════════════════════════════
  // UI THRESHOLDS (for button disabling and warnings)
  // ═══════════════════════════════════════════════════
  TRAIN_ENERGY_MINIMUM: 10,           // Minimum energy to show training option
  CLEAN_CLEANLINESS_MAX: 90,          // Max cleanliness before clean button disabled
  REFILL_ENERGY_MINIMUM: 50,          // Minimum energy before refill disabled
  FEED_WARNING_THRESHOLD: 90,         // Hunger threshold for overfeeding warning
  MEDICINE_HEALTH_MINIMUM: 50,        // Health threshold for medicine warning (when not sick)

  // Mood indicator thresholds (PetProfilePanel display)
  MOOD_STAT_LOW_THRESHOLD: 30,        // Below this: show dirty/hungry/tired mood
  MOOD_HAPPINESS_HIGH_THRESHOLD: 80,  // Above this: show happy mood

  // Keyboard shortcut thresholds (PetProfilePanel quick actions)
  KEYBOARD_TRAIN_ENERGY_MIN: 30,      // Min energy for 't' shortcut
  KEYBOARD_CLEAN_THRESHOLD: 70,       // Max cleanliness for 'c' shortcut
  KEYBOARD_REFILL_THRESHOLD: 70,      // Max energy for 'r' shortcut

  // ═══════════════════════════════════════════════════
  // TIME CONSTANTS (milliseconds)
  // ═══════════════════════════════════════════════════
  ACTION_SPAM_WINDOW_MS: 500,               // 500ms - prevents double-clicks but feels instant
  TICK_INTERVAL_MS: 600000,                 // 10 minutes (unified for all stages except egg)
  REST_MAX_DURATION_MS: 36000000,           // 10 hours
  ENERGY_REFILL_COOLDOWN_MS: 86400000,      // 24 hours
  ABANDONED_STREAK_BREAK_MS: 172800000,     // 48 hours - time until abandoned pet loses streak

  // ═══════════════════════════════════════════════════
  // STAT DECAY RATES
  // ═══════════════════════════════════════════════════
  // Design: Unified stat decay - all stats decay equally.
  // Tick interval: 10 minutes (TICK_INTERVAL_MS) for all stages except eggs,
  // which don't decay → 6 ticks/hour, 144 ticks/day.
  //
  // Care-runway target: decay is HALF the original rate, so a baby survives a
  // ~6h stretch and an adult a ~12h stretch before dropping to
  // HUNGER_NEGLECT_THRESHOLD (25) and taking health damage. That clears the
  // old round-the-clock 3-hour leash without making a pet care for itself.
  //   Baby  (Stage 0-1) @ 2.0/tick: 100 → 25 in 37.5 ticks = 6h 15m  (zero at 8h 20m)
  //   Adult (Stage 2+)  @ 1.0/tick: 100 → 25 in 75 ticks   = 12h 30m (zero at 16h 40m)
  // Adults stay hardier than babies (half the decay), as before.

  // Unified decay for all stats (hunger, happiness, energy, cleanliness, discipline)
  STAT_DECAY_BABY: 2.0,             // 50 ticks to zero (~8.3 hours @ 10-min ticks)
  STAT_DECAY_ADULT: 1.0,            // 100 ticks to zero (~16.7 hours @ 10-min ticks)
  ENERGY_REGEN_PER_TICK: 50,      // Full restore in 2 ticks (20 min @ 10-min ticks) when resting

  // Happiness-specific decay multipliers (applied to base decay).
  // Happiness is deliberately NOT slowed by the care-runway change above: it is
  // self-healing via the thriving gain below, so it was never what forced a
  // player back. These multipliers are re-derived from the new base decay to
  // hold the effective rate at exactly the pre-change 0.2604/tick.
  // Baby:  2.0 × 0.1302 = 0.2604/tick × 144 = 37.5/day
  // Adult: 1.0 × 0.2604 = 0.2604/tick × 144 = 37.5/day
  HAPPINESS_DECAY_MULTIPLIER_BABY: 0.1302,  // Baby happiness decay multiplier
  HAPPINESS_DECAY_MULTIPLIER_ADULT: 0.2604, // Adult happiness decay multiplier

  // Happiness passive gain when thriving (health, hunger, energy all >= 25).
  // Different rates for baby vs adult; babies recover twice as fast.
  // Net rates below are gain minus the 0.2604/tick happiness decay, at the real
  // 6 ticks/hour (10-min ticks):
  // Baby thriving:  +1.84/tick - 0.2604/tick = +1.5796/tick net → ~+9.5/hour
  HAPPINESS_GAIN_PER_TICK_THRIVING_BABY: 1.84, // Baby thriving bonus (stage 0-1)

  // Adult thriving: +0.92/tick - 0.2604/tick = +0.6596/tick net → ~+4.0/hour
  HAPPINESS_GAIN_PER_TICK_THRIVING_ADULT: 0.92, // Adult thriving bonus (stage 2+)

  // Legacy constant (kept for backwards compatibility, uses adult value)
  HAPPINESS_GAIN_PER_TICK_THRIVING: 0.92, // Use stage-specific constants above instead

  // Happiness penalty when neglected or dirty (applied per tick)
  HAPPINESS_STATUS_PENALTY: 2, // -2 happiness per tick if cleanliness < 50 OR hunger < 50
  HAPPINESS_STATUS_PENALTY_THRESHOLD: 50, // Threshold for applying happiness penalty

  // Health (conditional decay based on neglect)
  HEALTH_DECAY_NEGLECTED: 1.0,      // ~16.7 hours to zero when neglected (100 ticks @ 10-min)
  HEALTH_DECAY_CRITICAL: 2.0,       // ~8.3 hours to zero when critically neglected (50 ticks @ 10-min)

  // ═══════════════════════════════════════════════════
  // REST MECHANICS
  // ═══════════════════════════════════════════════════
  REST_DECAY_MULTIPLIER_PERCENT: 20,        // 80% reduction in happiness decay when resting
  REST_HUNGER_DECAY_MULTIPLIER_PERCENT: 50, // 50% reduction in hunger decay when resting
  SLEEP_EARLY_WAKE_HAPPINESS_PENALTY: 10,   // Happiness penalty for waking before full rest
  ENERGY_EARLY_WAKE_PENALTY_THRESHOLD: 80,  // Energy threshold for early wake penalty
  AUTO_WAKE_INACTIVITY_MS: 28800000,        // 8 hours - auto wake when energy=100 AND inactive this long
  AUTO_WAKE_MAX_REST_MS: 172800000,         // 48 hours - unconditional auto wake after this long resting

  // ═══════════════════════════════════════════════════
  // SUIKA RESOURCE SYSTEM
  // ═══════════════════════════════════════════════════
  SUIKA_CONSUMPTION_PER_TICK: 0.05, // Suika consumed per tick (reduced for 50/50 balance)
  SUIKA_INITIAL_BALANCE: 1000,      // Starting balance for purchased pets
  SUIKA_INITIAL_BALANCE_FREE: 500,  // Starting balance for found eggs and airdrops
  SUIKA_PER_USDC: 1000,              // Base conversion rate: 1 USDC = 1000 Suika

  // "Low on energy" threshold: at/below this balance a pet is framed as needing
  // more SUIKA to keep playing, and the warm "add SUIKA" care prompt surfaces.
  // (Replaces the former hardcoded `balance < 100` check in PetGasBanner.)
  SUIKA_LOW_THRESHOLD: 100,

  // Web2: the game is free, so a player who runs low is refilled for free — but only once the
  // wallet is BELOW SUIKA_LOW_THRESHOLD, and only up to this balance: enough to keep fighting
  // (a battle costs SUIKA_COST_TRIAL / SUIKA_COST_RANKED), never toward a skill. **100, down from
  // 500 (Beta 21.53)**: once skills cost Suika only, a 500 top-up paid half of every copy after
  // the first (owner, 2026-10-26: "shrink it"). Kept equal to SUIKA_LOW_THRESHOLD on purpose.
  SUIKA_FREE_REFILL_TARGET: 100,

  // Post-ownership starter balance. Free play never gates on SUIKA — the SUIKA
  // "energy" economy intentionally begins at OWNERSHIP. When a player pays to own
  // their pet (promoteFreePetToChain), the owned pet starts deliberately LOW
  // (at/under SUIKA_LOW_THRESHOLD) so the very first guided owner action is the warm
  // "give {name} energy to keep playing" step, not a silent full tank.
  SUIKA_OWNED_STARTER: 50,

  // Action costs (Suika deducted per action)
  // Rebalanced for ~50/50 split between time-based and action-based costs
  // **CARE IS FREE FOR NOW (Beta 20.22).** Suika is what a player spends on skill cards, so a
  // care action that took it away made looking after a Suikamon a tax on collecting. Every care
  // price is 0 until the economy is redesigned; `careActionSuikaCost` reads these and a cost of 0
  // skips the charge entirely (no wallet call, nothing to refund). Restore a price HERE and the
  // server charges it and the Help page prints it — nothing else has to change.
  SUIKA_COST_FEED_MEAL: 0,          // Cost to feed meal
  SUIKA_COST_FEED_SNACK: 0,         // Cost to feed snack
  SUIKA_COST_CLEAN: 0,              // Cost to clean pet
  SUIKA_COST_TRAIN: 0,              // Cost to train pet
  SUIKA_COST_MEDICINE: 0,           // Cost to give medicine
  SUIKA_COST_REST: 0,               // Cost to start rest
  SUIKA_COST_ENERGY_REFILL: 0,      // Cost to refill energy
  SUIKA_COST_TRIAL: 1,              // Cost to start a trial battle (free to play)
  SUIKA_COST_RANKED: 1,             // Cost to start a ranked battle (challenger pays, charged on confirmed match, no refund on loss)
  SUIKA_COST_RENAME: 50,            // Cost to rename a pet (player-facing only, admin tool is free)

  // ═══════════════════════════════════════════════════
  // EGG DONATION & HATCHED PET SALE
  // ═══════════════════════════════════════════════════
  EGG_DONATION_SUIKA_REWARD: 100,        // SUIKA credited to trainer for donating an egg
  // One Suikamon per species: a found egg that rolls a species the player already owns never
  // becomes a second pet — it is paid out as this much SUIKA into the player's wallet instead.
  DUPLICATE_EGG_SUIKA_REWARD: 1000,
  HATCHED_PET_SALE_BASE_SUIKA: 50,        // Base SUIKA for selling a hatched Suikamon
  HATCHED_PET_SALE_SUIKA_PER_LEVEL: 10,   // Additional SUIKA per level (final price = base + level * perLevel)
  TRAINER_SUIKA_TRANSFER_INCREMENT: 100,  // SUIKA must be transferred in increments of 100
  DEPOSIT_REWARD_SUIKA: 100,             // One-time SUIKA reward for moving an existing pet into custody (first pending_deposit → in_custody)

  // ═══════════════════════════════════════════════════
  // HATCHING
  // ═══════════════════════════════════════════════════
  /**
   * **WHAT A NEWBORN FEELS LIKE THE SECOND IT HATCHES.** One number per care stat.
   *
   * It used to be 30 across the board, which put happiness under HAPPINESS_SAD_THRESHOLD (50)
   * and cleanliness at two poops: the very first thing a new player saw after warming their
   * egg was a crying Suikamon sitting in mess — and the companion bubble then scolded them for
   * neglect. Nobody had neglected anything yet.
   *
   * Now the newborn is HAPPY, CLEAN and FULL OF ENERGY, but HUNGRY:
   *   • hunger 40 — under HUNGER_XP_THRESHOLD_FULL (50), so the first feed is the obvious
   *     first action AND pays full XP. One clear thing to do, not five.
   *   • happiness 70 — above the sad line, so the mood badge reads Happy on first sight.
   *   • energy 80 — enough for a battle or two straight away; the fights are the best part
   *     of the game and a new player must not be told "too tired" before trying one.
   *   • health 70, cleanliness 80 — no poop on day one, and Clean still has room to matter.
   *   • discipline 40 — above UNRULY (25), with training still worth doing.
   * Raising it from here is still the first lesson; it just starts with a pet that likes you.
   */
  HATCH_INITIAL_CARE_STATS: {
    hunger: 40,
    happiness: 70,
    energy: 80,
    health: 70,
    cleanliness: 80,
    discipline: 40,
  },

  // ═══════════════════════════════════════════════════
  // EVOLUTION
  // ═══════════════════════════════════════════════════
  /**
   * Every care stat a Suikamon carries — hunger, happiness, energy, health,
   * cleanliness, discipline — is RESTORED to this on a stage evolution.
   *
   * Evolving is the loop's milestone: the Suikamon is remade, so it comes out of it
   * fed, rested and clean rather than carrying the neglect of the grind that earned
   * it. 90 and not 100 on purpose — a full bar has nothing to care for, and the care
   * loop (and its SUIKA income) has to still have something to do the minute the new
   * stage lands.
   *
   * HATCHING IS NOT AN EVOLUTION for this purpose: a newborn still starts at
   * {@link HATCH_INITIAL_CARE_STATS} (happy and rested, but hungry), because raising it
   * from there is the first lesson the game teaches.
   */
  EVOLUTION_RESTORED_STATS: 90,

  // ═══════════════════════════════════════════════════
  // RARITY SUPPLY CAPS (per species)
  // ═══════════════════════════════════════════════════
  RARITY_CAP_COMMON: 500,
  RARITY_CAP_RARE: 100,
  RARITY_CAP_EPIC: 50,
  RARITY_CAP_LEGENDARY: 10,
  RARITY_CAP_UNIQUE: 1,

  // ═══════════════════════════════════════════════════
  // BREEDING SYSTEM
  // ═══════════════════════════════════════════════════
  BREEDING_LEVEL_INTERVAL: 3,        // Breeding token unlocks every N levels (3 = every 3 levels)
  INITIAL_EGG_LEVEL,                 // Reference to standalone constant
  INITIAL_EGG_STAGE,                 // Reference to standalone constant
  NO_INITIAL_PAYMENT,                // Reference to standalone constant
  DEFAULT_PARENT_RARITY,             // Reference to standalone constant

  // ═══════════════════════════════════════════════════
  // RANDOMNESS SYSTEM
  // ═══════════════════════════════════════════════════
  RANDOM_VALUE_MAX,                  // Reference to standalone constant
  MAX_RARITY,                        // Reference to standalone constant
  PROBABILITY_DECIMAL_PLACES,        // Reference to standalone constant

  // Seed character slicing positions for deterministic randomness
  SEED_RARITY_START,                 // Reference to standalone constant
  SEED_RARITY_END,                   // Reference to standalone constant
  SEED_SPECIES_START,                // Reference to standalone constant
  SEED_SPECIES_END,                  // Reference to standalone constant
  SEED_FALLBACK_START,               // Reference to standalone constant
  SEED_FALLBACK_END,                 // Reference to standalone constant
  SEED_ULTIMATE_START,               // Reference to standalone constant
  SEED_ULTIMATE_END,                 // Reference to standalone constant

  // ═══════════════════════════════════════════════════
  // CHECKPOINT RANDOMNESS (Sui Blockchain)
  // ═══════════════════════════════════════════════════
  CHECKPOINT_POLL_INTERVAL_MS,       // Reference to standalone constant
  CHECKPOINT_INTERVAL_SECONDS,       // Reference to standalone constant
  DEFAULT_CHECKPOINTS_AHEAD,         // Reference to standalone constant
  DEFAULT_MAX_WAIT_MS,               // Reference to standalone constant
} as const;

// =====================================================
// Suika Replenishment Tiers (Shared between frontend and backend)
// =====================================================

/**
 * Suika replenishment tier configuration.
 * Single source of truth for pricing and Suika amounts.
 *
 * Structure:
 * - usdcAmount: Cost in USDC
 * - suikaAmount: Suika received (may include promotional bonus)
 * - title: Display name for the tier
 * - description: Short description
 */
export interface SuikaReplenishTier {
  usdcAmount: number;
  suikaAmount: number;
  title: string;
  description: string;
}

export const SUIKA_REPLENISH_TIERS: Record<string, SuikaReplenishTier> = {
  STARTER: {
    usdcAmount: 2,
    suikaAmount: 2000,    // 2 USDC = 2,000 Suika (base rate)
    title: "Starter Pack",
    description: "Starter Pack",
  },
  STANDARD: {
    usdcAmount: 9,
    suikaAmount: 12000,   // 9 USDC = 12,000 Suika (+33% bonus)
    title: "Player Pack",
    description: "Player Pack",
  },
  PREMIUM: {
    usdcAmount: 25,
    suikaAmount: 40000,   // 25 USDC = 40,000 Suika (+60% bonus)
    title: "Supporter Pack",
    description: "Supporter Pack 💖",
  },
} as const;

/**
 * Get Suika amount for a given USDC payment.
 * Checks if payment matches a tier, otherwise uses base conversion rate.
 *
 * @param usdcAmount - Amount of USDC paid
 * @returns Suika amount to credit
 */
export function getSuikaAmountForPayment(usdcAmount: number): number {
  // Check for tier match (with tolerance for floating point)
  const TOLERANCE = 0.01;

  for (const tier of Object.values(SUIKA_REPLENISH_TIERS)) {
    if (Math.abs(usdcAmount - tier.usdcAmount) <= TOLERANCE) {
      return tier.suikaAmount;
    }
  }

  // No tier match - use base conversion rate
  return Math.floor(usdcAmount * GAME_CONSTANTS.SUIKA_PER_USDC);
}

/**
 * Breeding Rarity Weights
 * Offspring rarity probabilities based on parent's species rarity.
 * Key: parent rarity number (0=Common, 1=Rare, 2=Epic, 3=Legendary, 4=Unique)
 * Value: Record mapping offspring rarity (0-4) to percentage weight
 *
 * Probability Table:
 * Parent rarity   Common   Rare   Epic   Legendary   Unique
 * ----------------------------------------------------------
 * Common           97%      3%     0%      0%          0%
 * Rare             74%     20%     5%      1%          0%
 * Epic             67%     25%     7%      1%          0%
 * Legendary        62%     25%    10%      3%          0%
 * Unique           74%     20%     5%      1%          0%
 */
export const BREEDING_RARITY_WEIGHTS: Record<number, Record<number, number>> = {
  // Common parent: near-guaranteed common offspring (F2P friendly)
  0: { 0: 97, 1: 3, 2: 0, 3: 0, 4: 0 },
  // Rare parent: common and rare mix with small legendary chance
  1: { 0: 74, 1: 20, 2: 5, 3: 1, 4: 0 },
  // Epic parent: focused on epic and rare
  2: { 0: 67, 1: 25, 2: 7, 3: 1, 4: 0 },
  // Legendary parent: balanced rarity distribution
  3: { 0: 62, 1: 25, 2: 10, 3: 3, 4: 0 },
  // Unique parent: same as rare
  4: { 0: 74, 1: 20, 2: 5, 3: 1, 4: 0 },
};

// =====================================================
// Referral System Constants
// =====================================================

/**
 * Referral system payment split configuration.
 *
 * When a user adopts with a valid referral:
 * - User pays 90% of original price (10% discount)
 * - Referral receives 10% of original price (commission)
 * - Backend receives 80% of original price
 *
 * All percentages are relative to the ORIGINAL (non-discounted) price.
 */
export const REFERRAL_CONSTANTS = {
  /** Discount given to user when using referral (always 10% off original price) */
  USER_DISCOUNT_PERCENT: 10,
  /** Default commission for open referrers (any pet owner, 35% of base price) */
  REFERRER_COMMISSION_PERCENT: 35,
  /** Maximum discount percentage that can be used with non-Common pets (81% or more requires Common) */
  HIGH_DISCOUNT_LIMIT_PERCENT: 81,
} as const;

/**
 * Calculate referral payment split percentages.
 * User always gets 10% discount. Referral commission is per-partner (from env).
 * Backend receives the remainder: 100 - userDiscount - referralCommission.
 *
 * @param referralCommissionPercent - Commission percentage for this referral partner (from env)
 * @returns Object with backend and referral percentages
 */
export function calculateReferralSplit(referralCommissionPercent: number): {
  userPaysPercent: number;
  backendPercent: number;
  referralPercent: number;
} {
  const userDiscount = REFERRAL_CONSTANTS.USER_DISCOUNT_PERCENT;
  const userPaysPercent = 100 - userDiscount;
  const backendPercent = 100 - userDiscount - referralCommissionPercent;

  return {
    userPaysPercent,
    backendPercent,
    referralPercent: referralCommissionPercent,
  };
}

/**
 * Calculate the SUIKA reward for selling a hatched Suikamon to the reserve.
 * Formula: base + (level * perLevel). Defaults yield 50 + 10 * level
 * (e.g. level 1 = 60 SUIKA, level 20 = 250 SUIKA).
 */
export function calculateHatchedPetSalePrice(level: number): number {
  const safeLevel = Math.max(0, Math.floor(level));
  return (
    GAME_CONSTANTS.HATCHED_PET_SALE_BASE_SUIKA +
    safeLevel * GAME_CONSTANTS.HATCHED_PET_SALE_SUIKA_PER_LEVEL
  );
}
